import { getCookie, setCookie } from '../../../utility_objects/CookieManager';

/**
 * Scelte del riquadro Visualizzazione (pulsante Visualizzazione della barra
 * delle viste), una per calendario: ogni calendario con la griglia oraria ha
 * le sue Ore e, dove c'è la fascia, le sue Presenze. Cambiarle in un
 * calendario non tocca gli altri; tornando in un calendario si ritrova la sua
 * scelta. Ognuna è ricordata per PC in un cookie proprio, per un anno; senza
 * cookie, o con un valore sconosciuto, vale lo standard: Ore «Tutte»,
 * Presenze «Nessuno».
 *
 * I nomi dei cookie: getCookie cerca nome + '=' per sottostringa, quindi
 * nessun nome può finire con un altro nome usato nel pacchetto, né il
 * contrario (controllo in preferenzeCalendario.spec).
 */

/** Calendari con la griglia oraria; la scheda cliente non ha scelte. */
export type CalendarioId = 'negozio' | 'personale' | 'studio' | 'utente';

/** Calendari con la fascia Presenze: agenda negozio e giornaliero Studio. */
export type CalendarioConPresenze = Extract<CalendarioId, 'negozio' | 'studio'>;

export const haPresenze = (c: CalendarioId): c is CalendarioConPresenze =>
  c === 'negozio' || c === 'studio';

/**
 * Modalità della fascia Presenze, scelta nella riga Presenze del riquadro
 * Visualizzazione: «Nessuno» (nessuna fascia), «Presenze» (una chip per
 * persona col solo nome) o «Totali» (una chip per persona col testo
 * completo).
 */
export type PresenzeModo = 'nessuna' | 'presenze' | 'totali';

/**
 * Cookie della funzione «Solo orario» (riga Ore): '1' «Prenotabili», '0'
 * «Tutte». L'agenda negozio tiene il nome di prima.
 */
export const COOKIE_SOLO_ORARIO: Record<CalendarioId, string> = {
  negozio: 'defaultCalendarSoloOrario',
  personale: 'personalCalendarSoloOrario',
  studio: 'studioCalendarSoloOrario',
  utente: 'userCalendarSoloOrario',
};

/**
 * Cookie della modalità della fascia Presenze (riga Presenze): 'nessuna',
 * 'presenze' o 'totali'. L'agenda negozio tiene il nome di prima.
 */
export const COOKIE_PRESENZE_MODO: Record<CalendarioConPresenze, string> = {
  negozio: 'defaultCalendarPresenzeModo',
  studio: 'studioCalendarPresenzeModo',
};

/** Durata dei cookie delle scelte, in ore: un anno. */
export const DURATA_COOKIE_ORE = 24 * 365;

/** Ore «Prenotabili» solo col cookie a '1': altrimenti «Tutte». */
export const getSoloOrarioSalvato = (calendario: CalendarioId): boolean =>
  getCookie(COOKIE_SOLO_ORARIO[calendario]) === '1';

/** Scrive la scelta delle Ore del calendario: '1' o '0'. */
export const salvaSoloOrario = (
  calendario: CalendarioId,
  acceso: boolean
): void =>
  setCookie(
    COOKIE_SOLO_ORARIO[calendario],
    acceso ? '1' : '0',
    DURATA_COOKIE_ORE
  );

/**
 * Modalità salvata della fascia Presenze del calendario; senza un valore
 * salvato, o con uno sconosciuto, nessuna fascia. Il cookie
 * defaultCalendarPresenze del vecchio interruttore non conta più.
 */
export const getPresenzeModoSalvato = (
  calendario: CalendarioConPresenze
): PresenzeModo => {
  const valore = getCookie(COOKIE_PRESENZE_MODO[calendario]);
  return valore === 'presenze' || valore === 'totali' ? valore : 'nessuna';
};

/** Scrive la modalità della fascia Presenze del calendario. */
export const salvaPresenzeModo = (
  calendario: CalendarioConPresenze,
  modo: PresenzeModo
): void => setCookie(COOKIE_PRESENZE_MODO[calendario], modo, DURATA_COOKIE_ORE);
